//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelQuestionsAndAnswers as APIModelQnA } from "wint-api-client/dist/types";
import { Question, QuestionGroup } from "models/propTypes/genericFormProps";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
import * as Yup from "yup";
import { FormHelper } from "utils/helpers";

function useDynamicFormValidationSchema(questionGroups: QuestionGroup[]) {
  const getFieldValidationSchema = (question: Question) => {
    switch (question.answerKind) {
      case APIModelQnA.AnswerKind.Boolean:
        return Yup.number();
      case APIModelQnA.AnswerKind.Text:
      case APIModelQnA.AnswerKind.SingleLineText:
        return Yup.string();
      case APIModelQnA.AnswerKind.Numeric:
        return Yup.number();
      case APIModelQnA.AnswerKind.Selection:
        return question.selectionDefinition?.multiSelection ? Yup.array() : Yup.number();
      case APIModelQnA.AnswerKind.StringSelection:
        return question.stringSelectionDefinition?.multiSelection ? Yup.array() : Yup.string();
      case APIModelQnA.AnswerKind.File:
        return Yup.array().of(
          Yup.object<UploadedFile>({
            contentType: Yup.string(),
            fileName: Yup.string(),
            fileSize: Yup.number(),
            data: Yup.string(),
          })
        );
      case APIModelQnA.AnswerKind.Matrix:
        return Yup.array().of(
          Yup.object<any>(getMatrixValidationSchema(question.matrixDefinition?.questionMatrixColumnDefinitions ?? []))
        );
    }
  };

  const getMatrixValidationSchema = (columnDefinitions: APIModelQnA.QuestionMatrixColumnDefinitionModel[]) => {
    const fields: Record<string, Yup.Schema> = {};

    const getSchema = (valueKind: APIModelQnA.QuestionMatrixValueKind) => {
      switch (valueKind) {
        case APIModelQnA.QuestionMatrixValueKind.Boolean:
          return Yup.boolean();
        case APIModelQnA.QuestionMatrixValueKind.Text:
          return Yup.string();
        case APIModelQnA.QuestionMatrixValueKind.Numeric:
          return Yup.number();
      }
    };

    columnDefinitions.forEach((c) => {
      fields[`${c.ordinal}`] = getSchema(c.valueKind);
    });

    return fields;
  };

  const getOptionalQuestionGroupSchema = (dependantFieldId: string, schema: Yup.Schema<any>) => {
    return schema.when(
      FormHelper.getDynamicFormFieldName(dependantFieldId),
      ([value], schema: any) => (value && schema.required(TranslationKeys.Validations.Required)) || schema
    );
  };

  const getQuestionGroupValidationSchema = (fields: Record<string, Yup.Schema>, qg: QuestionGroup) => {
    const questions = qg.questions ?? [];
    const dependantFieldId = qg.isOptional ? qg.id : undefined;

    if (dependantFieldId) {
      fields[FormHelper.getDynamicFormFieldName(dependantFieldId)] = Yup.number().required(
        TranslationKeys.Validations.Required
      );
    }

    questions.forEach((q) => {
      if (q.isHidden) {
        return;
      }

      const schema = getFieldValidationSchema(q);
      fields[FormHelper.getDynamicFormFieldName(q.id!)] =
        dependantFieldId && q.isRequired
          ? getOptionalQuestionGroupSchema(dependantFieldId, schema)
          : q.isRequired
          ? schema.required(TranslationKeys.Validations.Required)
          : schema;
    });
  };

  const isHiddenSubQuestionGroup = (hiddenParents: string[], child: string | undefined): boolean =>
    hiddenParents.some((p) => child?.startsWith(p));

  const getValidationSchemaObj = () => {
    const fields: Record<string, Yup.Schema> = {};

    const hiddenQuestionGroupLevels = questionGroups
      .filter((qg) => qg.isHidden && qg.level !== undefined)
      .map((qg) => qg.level ?? "");

    questionGroups.forEach((qg) => {
      if (isHiddenSubQuestionGroup(hiddenQuestionGroupLevels, qg.level)) {
        return;
      }

      getQuestionGroupValidationSchema(fields, qg);
    });

    return fields;
  };

  return { getValidationSchemaObj };
}

export default useDynamicFormValidationSchema;
